import { useCallback, useEffect, useRef, useState } from 'react';
import { store } from '../store';
import { CalendarBot } from '../services/calendarWebSocketService';
import { fetchCallRecord } from '../services/participantReviewService';
import { sessionManager } from '../services/sessionManager';
import {
  buildReviewRows,
  ReviewRosterEntry,
  ReviewRow,
  ReviewTranscript,
  shouldReviewParticipants,
} from '../utils/participantReview';
import { useSessionSummary } from './useSessionSummary';
import { ENDED_BOT_STATUSES } from '../utils/endedSelectedBot';

const DEFAULT_SESSION_NAME = 'Session';


export interface EndOfCallRequest {
  /** conversationsessions.sessionId — for a bot session, the bot id. */
  sessionId: string | null;
  sessionName?: string;
  /** Captured by the caller before teardown resets it. */
  isCreator: boolean;
}

/**
 * An end reported outside the app: a calendar bot_terminated / bot_failed (a
 * whole CalendarBot), or the recall socket's session_ended (only the room id,
 * reported as `terminated`).
 */
export type OutsideEndReport = Pick<CalendarBot, 'botId' | 'status'> & { eventTitle?: string };

/** The page's bot state at the moment an outside end is noticed. */
export interface ConnectedBotState {
  isBotConnected: boolean;
  connectedBotIds: Array<string | null>;
  isSessionCreator: boolean;
}

export interface PendingParticipantReview {
  sessionId: string;
  sessionName: string;
  isCreator: boolean;
  rows: ReviewRow[];
  roster: ReviewRosterEntry[];
}

/**
 * Rows for the review, from the saved call record (the live store keeps only
 * the last 50 transcripts) or, if that is unavailable, the live store snapshot
 * taken when the call ended. Null when there is nothing to review.
 */
const loadParticipantReview = async (
  sessionId: string,
  liveConversation: { participants: ReviewRosterEntry[]; transcripts: ReviewTranscript[] },
): Promise<{ rows: ReviewRow[]; roster: ReviewRosterEntry[] } | null> => {
  const savedRecord = await fetchCallRecord(sessionId);
  const roster = savedRecord?.participants?.length ? savedRecord.participants : liveConversation?.participants || [];
  const transcripts = savedRecord?.transcripts || liveConversation?.transcripts || [];
  const rows = buildReviewRows(roster, transcripts);
  return shouldReviewParticipants(rows) ? { rows, roster } : null;
};

/** True when `bot` reports an end for the bot this page is connected to. */
export const isConnectedBotEnded = (bot: OutsideEndReport | null | undefined, live: ConnectedBotState): boolean =>
  !!bot?.botId &&
  live.isBotConnected &&
  ENDED_BOT_STATUSES.includes(bot.status) &&
  live.connectedBotIds.includes(bot.botId);

/**
 * The one end-of-call flow for bot sessions, however the call ended (Remove
 * Bot in the app, or the bot leaving/terminated outside it): the creator's
 * "Review participants" sheet when someone besides them spoke, then the
 * summary. Each session id runs the flow once, so both paths firing for the
 * same call show it once.
 */
export function useEndOfCallFlow() {
  const summary = useSessionSummary();
  const { computeAndShowSummary } = summary;
  const endedSessionIdsRef = useRef<Set<string>>(new Set());
  const [participantReview, setParticipantReview] = useState<PendingParticipantReview | null>(null);
  // The summary is computed on the render AFTER it is requested, so it reads
  // the store as updated by a review save (computeAndShowSummary closes over it).
  const [pendingSummaryName, setPendingSummaryName] = useState<string | null>(null);

  useEffect(() => {
    if (pendingSummaryName === null) return;
    setPendingSummaryName(null);
    computeAndShowSummary(pendingSummaryName);
  }, [pendingSummaryName, computeAndShowSummary]);

  const startEndOfCall = useCallback(async ({ sessionId, sessionName, isCreator }: EndOfCallRequest) => {
    const name = sessionName || DEFAULT_SESSION_NAME;
    if (sessionId) {
      if (endedSessionIdsRef.current.has(sessionId)) return;
      endedSessionIdsRef.current.add(sessionId);
    }
    // Snapshot now: the caller is about to tear the session down.
    const liveConversation = store.getState().conversation;
    const review = isCreator && sessionId ? await loadParticipantReview(sessionId, liveConversation) : null;
    if (review && sessionId) {
      setParticipantReview({ ...review, sessionId, sessionName: name, isCreator });
    } else {
      setPendingSummaryName(name);
    }
  }, []);

  /** An outside end (calendar bot_terminated / status change, recall session_ended) for the connected bot. */
  const endFromOutside = useCallback(
    (bot: OutsideEndReport, live: ConnectedBotState): boolean => {
      if (!isConnectedBotEnded(bot, live)) return false;
      sessionManager.setCallEndTime();
      startEndOfCall({ sessionId: bot.botId, sessionName: bot.eventTitle, isCreator: live.isSessionCreator }).catch(
        (error) => console.error('[useEndOfCallFlow] End-of-call flow failed:', error)
      );
      return true;
    },
    [startEndOfCall]
  );

  /**
   * A bot that was never let in (owner decision 2026-10-03): there was no call,
   * so no review or summary for it, now or from an end report that follows.
   */
  const skipEndOfCall = useCallback((sessionId: string) => {
    if (sessionId) endedSessionIdsRef.current.add(sessionId);
  }, []);

  const finishParticipantReview = useCallback(() => {
    setPendingSummaryName(participantReview?.sessionName || DEFAULT_SESSION_NAME);
    setParticipantReview(null);
  }, [participantReview]);

  return {
    ...summary,
    participantReview,
    startEndOfCall,
    endFromOutside,
    skipEndOfCall,
    finishParticipantReview,
  };
}
